import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { Button, Dropdown, Form } from "react-bootstrap";
import moment from "moment";
import { useSelector } from "react-redux";
import { db } from "../../db";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimeField } from "@mui/x-date-pickers/DateTimeField";
import { SingleInputDateTimeRangeField } from "@mui/x-date-pickers-pro/SingleInputDateTimeRangeField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useObserveDbTable } from "../../hooks/useMemoryDB";
import { useMainContext } from "../../hooks/useMainContext";
import { useItemContext } from "../../hooks/useItem";
import useUserFields from "../../hooks/useUserFields";
import RotaryKnob from "../rotaryKnob";
import Select from "../selects/selectWithSearch";
import Container from "../container";
import { dashboardUrl } from "./dashboard";
import {
  hasActiveVariants,
  isLegacy,
  isOpen,
  isStarted,
  referenceOf
} from "./links";
import {
  assignmentOf,
  controlVariation,
  hasWeights,
  isArchived,
  newVariation,
  quotaOf,
  statusOf,
  strategyOf
} from "./model";
import { isPendingPage, selectablePages } from "./pages";
import { archive, closedOutcome, promotedOutcome } from "./outcome";
import styles from "./styles.module.scss";
import "../../scss/calendar.css";
import "../../scss/time.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faFlask,
  faPowerOff,
  faTrafficLight
} from "@fortawesome/free-solid-svg-icons";
import idGen from "../../utils/idGen";

const headerKW = (k, v) => {
  if (!v) return null;

  return (
    <div className={styles.element}>
      <div className={styles.label}>{k}</div>
      <div className={styles.value}>{v}</div>
    </div>
  );
};

// The message of a field in error (the validation also gives `true`)
const fieldError = (message) =>
  typeof message === "string" ? (
    <div className={styles.fieldError}>{message}</div>
  ) : null;

const pageTitle = (blockId) => db.peek("blocks", blockId)?.title ?? "";

const formatDate = (date) => moment(date).format("DD-MM-YYYY HH:mm");

// The stored experiment of the form: the row of the id the form was opened on
// (none for a new experiment, also the copy of another one, whose values carry
// the id and the history of the source). What is locked and what is archived
// is decided by the stored row, not by the values being typed.
const storedExperimentOf = (id) =>
  id ? db.peek("experiments", id) : undefined;

export default function Experiment(props) {
  const { item } = props;
  const { values, errors, dirty, setFieldValue, setValues, submitForm } =
    useFormikContext();
  const { prevItemId, isCopy } = useItemContext();
  const { branch } = useUserFields("branch");
  const canWrite = useSelector(
    (state) =>
      state.user.permissions?.[state.user.tenant]?.write?.includes(
        "experiments"
      ) ?? false
  );
  const [showScheduling, setShowScheduling] = useState(
    values.scheduling ? true : false
  );
  const stored = storedExperimentOf(prevItemId);
  // The Save reads the id the form was opened on from here
  item.current = prevItemId;
  const started = isStarted(stored);
  const archived = isArchived(stored);
  const assignment = assignmentOf(values);

  // The Save that archives the experiment follows the confirmation: the
  // values are set first, the Save runs on the render that has them
  const [saveOutcome, setSaveOutcome] = useState(false);
  useEffect(() => {
    if (!saveOutcome || !values.outcome) return;

    setSaveOutcome(false);
    Promise.resolve(submitForm()).catch((error) =>
      console.error("⚠️ SAVE OF THE OUTCOME", error)
    );
  }, [saveOutcome, submitForm, values.outcome]);

  // Promote and Close save the experiment: nothing else may be waiting in the
  // form, and the user must be able to save
  const finishBlocker = dirty
    ? "Save your changes first."
    : !canWrite
      ? "You don't have the permission to save experiments."
      : branch === "main"
        ? "Saving is not allowed on main: switch to a branch."
        : null;

  const finish = useCallback(
    (outcome) => {
      setValues(archive(values, outcome));
      setSaveOutcome(true);
    },
    [setValues, values]
  );

  // The copy of an experiment starts as a draft of its own: no id, history
  // or outcome of the source (once, when the source has arrived)
  const copied = useRef(false);
  useEffect(() => {
    if (!isCopy || copied.current || !values.referenceId) return;

    copied.current = true;
    const { id, startedAt, outcome, archivedReference, ...rest } = values;
    setValues({
      ...rest,
      running: false,
      reference:
        referenceOf(db.peek("endpoints", values.referenceId)) ??
        values.reference ??
        archivedReference
    });
  }, [isCopy, setValues, values]);

  const handleChange = useCallback(
    (field, value) => {
      setFieldValue(field, value);
    },
    [setFieldValue]
  );

  // The control is the default page of the endpoint; the other variants
  // start again (their pages are the ones of the previous endpoint)
  const handleEndpointChange = useCallback(
    (id) => {
      const endpoint = db.peek("endpoints", id);

      if (started || archived || !endpoint || id === values.referenceId) return;
      setValues({
        ...values,
        referenceId: id,
        reference: referenceOf(endpoint),
        variations: [controlVariation(endpoint)]
      });
    },
    [archived, setValues, started, values]
  );

  const isValidRange = (range) => {
    const [start, end] = range;
    return start?.isValid() && end?.isValid();
  };

  const onChangeScheduling = useCallback(
    ([start, end]) => {
      if (!isValidRange([start, end])) return;

      setFieldValue("scheduling.start", start.toISOString());
      setFieldValue("scheduling.end", end.toISOString());
    },
    [setFieldValue]
  );

  const onChangeEnd = useCallback(
    (end) => {
      if (end?.isValid()) setFieldValue("scheduling.end", end.toISOString());
    },
    [setFieldValue]
  );

  const handleResetScheduling = useCallback(() => {
    setFieldValue("scheduling", undefined);
    setShowScheduling(false);
  }, [setFieldValue]);

  const schedulingValue = useMemo(() => {
    if (values.scheduling) {
      return [moment(values.scheduling.start), moment(values.scheduling.end)];
    }

    return [];
  }, [values.scheduling]);

  const endpoint = values.referenceId
    ? db.peek("endpoints", values.referenceId)
    : undefined;

  const settingHeader = useMemo(() => {
    const winner =
      values.outcome?.action === "promoted"
        ? values.variations?.find((v) => v.id === values.outcome.variationId)
        : undefined;

    return (
      <div className={styles.header}>
        {headerKW("Endpoint", values.reference ?? values.archivedReference)}
        {values.scheduling &&
          headerKW(
            "Scheduling",
            <>
              {moment(values.scheduling.start).format("DD-MM-YYYY")}
              <FontAwesomeIcon icon={faArrowRight} className={styles.arrow} />
              {moment(values.scheduling.end).format("DD-MM-YYYY")}
            </>
          )}
        {headerKW("Assigned by", assignment === "ares" ? "Ares" : "External")}
        {assignment === "ares" && headerKW("Quota", `${quotaOf(values)}%`)}
        {assignment === "ares" &&
          headerKW(
            "Strategy",
            strategyOf(values) === "custom" ? "Custom" : "Round Robin"
          )}
        {headerKW(
          "Status",
          <span className={styles.status}>
            {statusOf(values)}
            {winner ? `: ${winner.label}` : ""}
          </span>
        )}
      </div>
    );
  }, [assignment, values]);

  const range = (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="it">
      {archived ? (
        <div className={styles.lockedRange}>
          {formatDate(values.scheduling?.start)}
          <FontAwesomeIcon icon={faArrowRight} className={styles.arrow} />
          {formatDate(values.scheduling?.end)}
        </div>
      ) : started ? (
        // Started: the start is fixed, the end can move
        <div className={styles.lockedRange}>
          {formatDate(values.scheduling?.start)}
          <FontAwesomeIcon icon={faArrowRight} className={styles.arrow} />
          <DateTimeField
            value={moment(values.scheduling?.end)}
            onChange={onChangeEnd}
            error={Boolean(errors.scheduling)}
            hiddenLabel={true}
            className={styles.end}
          />
        </div>
      ) : (
        <SingleInputDateTimeRangeField
          value={schedulingValue}
          onChange={onChangeScheduling}
          error={Boolean(errors.scheduling)}
          hiddenLabel={true}
          className={styles.range}
        />
      )}
    </LocalizationProvider>
  );

  return (
    <>
      <Container
        title="Settings"
        header={settingHeader}
        headerOnlyOnFolded={false}
        folded={true}
        color="#3e1f47"
        coloredBars={false}
        warning={errors.settings}
        extra={
          <>
            <Form.Check
              type="switch"
              id="experimentRunning"
              checked={Boolean(values.running)}
              value="true"
              onChange={(e) => {
                const value = e.target.checked ? true : false;
                handleChange("running", value);
              }}
              disabled={archived}
              name="experimentRunning"
              label="Running"
            />
          </>
        }
      >
        <div className={styles.settings}>
          <div className={styles.row}>
            <div className={styles.cell}>
              <Form.Label className={styles.label}>Endpoint</Form.Label>

              <EndpointSelect
                selfId={stored?.id}
                value={values.referenceId}
                valueLabel={
                  referenceOf(endpoint) ??
                  values.reference ??
                  values.archivedReference
                }
                isInvalid={Boolean(errors.reference)}
                onChange={handleEndpointChange}
                disabled={started || archived}
              />
              {fieldError(errors.reference)}
            </div>
            <div className={styles.cell}>
              <Form.Label className={styles.label}>
                Scheduling{" "}
                {!started && !archived && (
                  <FontAwesomeIcon
                    icon={faPowerOff}
                    className={styles.resetScheduling}
                    onClick={handleResetScheduling}
                  />
                )}
              </Form.Label>
              {schedulingValue?.length === 0 && !showScheduling && (
                <Button
                  className="pick-date"
                  onClick={() => setShowScheduling(true)}
                  variant={
                    errors.scheduling ? "outline-danger" : "outline-light"
                  }
                >
                  Pick a Date Range...
                </Button>
              )}
              {showScheduling && <div>{range}</div>}
              {fieldError(errors.scheduling)}
            </div>
            <div className={styles.cell}>
              <Form.Label className={styles.label}>Assigned by</Form.Label>
              <Form.Control
                as="select"
                className={styles.select}
                onChange={(e) => handleChange("assignment", e.target.value)}
                value={assignment}
                disabled={started || archived}
              >
                <option value="ares">Ares</option>
                <option value="external">External service</option>
              </Form.Control>
            </div>
          </div>
          <div className={styles.row}>
            {assignment === "ares" ? (
              <>
                <div className={styles.cell}>
                  <Form.Label className={styles.label}>Quota (%)</Form.Label>
                  <Form.Control
                    type="number"
                    className={styles.select}
                    min={started ? quotaOf(stored) : 1}
                    max={100}
                    step={1}
                    value={quotaOf(values)}
                    onChange={(e) =>
                      handleChange(
                        "quota",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    isInvalid={Boolean(errors.quota)}
                    disabled={archived}
                  />
                  {fieldError(errors.quota)}
                  {started && (
                    <div className={styles.note}>
                      After the start the quota can only rise.
                    </div>
                  )}
                </div>
                <div className={styles.cell}>
                  <Form.Label className={styles.label}>
                    Traffic Strategy
                  </Form.Label>
                  <Form.Control
                    as="select"
                    className={styles.select}
                    onChange={(e) =>
                      handleChange("trafficStrategy", e.target.value)
                    }
                    value={strategyOf(values)}
                    disabled={started || archived}
                  >
                    <option value="roundRobin">Round Robin</option>
                    <option value="custom">Custom</option>
                  </Form.Control>
                </div>
                <div className={styles.cell}></div>
              </>
            ) : (
              <div className={styles.cell}>
                <div className={styles.note}>
                  The external service chooses the variation and sends its name:
                  the value of each variant must match the name of the
                  variant in the external service. Quota and traffic are decided
                  there.
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
      <Variations endpoint={endpoint} stored={stored} />
      <Outcome
        endpoint={endpoint}
        stored={stored}
        finishBlocker={finishBlocker}
        onFinish={finish}
      />
      <Results id={stored?.id} />
    </>
  );
}

// The select of the endpoint. An endpoint with active variants, or with the
// open experiment of another, is in the list but can't be chosen: it says
// why next to its name.
function EndpointSelect(props) {
  const { selfId, value, valueLabel, isInvalid, onChange, disabled } = props;
  const [text, setText] = useState("");
  const [changes, setChanges] = useState(0);
  const refresh = useCallback(() => setChanges((n) => n + 1), []);
  useObserveDbTable("endpoints", refresh);
  useObserveDbTable("experiments", refresh);
  useObserveDbTable("properties", refresh);

  const options = useMemo(() => {
    const busy = new Map();
    db.getTableRef("experiments")?.forEach((experiment) => {
      if (isOpen(experiment) && experiment.id !== selfId)
        busy.set(experiment.referenceId, experiment);
    });

    const needle = text.trim().toLowerCase();
    const rows = [...(db.getTableRef("endpoints")?.values() ?? [])];

    return rows
      .map((endpoint) => {
        const experiment = busy.get(endpoint.id);
        const hint = hasActiveVariants(endpoint)
          ? "has active variants"
          : experiment
            ? isLegacy(experiment)
              ? `old experiment ${experiment.title || experiment.id}: delete it`
              : `in experiment ${experiment.title || experiment.id}`
            : undefined;

        return {
          id: endpoint.id,
          label: referenceOf(endpoint) ?? endpoint.id,
          hint
        };
      })
      .filter(({ label }) => !needle || label.toLowerCase().includes(needle))
      .sort((a, b) => a.label.localeCompare(b.label));
    // changes: the tables are read in place
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changes, selfId, text]);

  // The dropdown opens even when disabled
  if (disabled)
    return (
      <Form.Control
        type="text"
        className={styles.select}
        value={valueLabel ?? ""}
        readOnly
        disabled
      />
    );

  return (
    <Select
      className={styles.select}
      name="select-experiment-endpoint"
      value={valueLabel}
      disabled={disabled}
      isInvalid={isInvalid}
      canFilter={true}
      onFilter={setText}
      onSelect={onChange}
    >
      {options.length === 0 ? (
        <div className="select-no-results" key="select-no-results">
          No Results...
        </div>
      ) : (
        options.map(({ id, label, hint }) => (
          <Dropdown.Item
            key={id}
            eventKey={id}
            active={id === value}
            disabled={Boolean(hint) && id !== value}
          >
            {label}
            {hint && <span className={styles.hint}> ({hint})</span>}
          </Dropdown.Item>
        ))
      )}
    </Select>
  );
}

// Dashboard esterna con i risultati dell'esperimento
function Results(props) {
  const { id } = props;
  const tenant = useSelector((state) => state.user.tenant);
  const src = dashboardUrl(
    process.env.REACT_APP_EXPERIMENTS_DASHBOARD_URL,
    tenant,
    id
  );

  if (!id) return null;

  return (
    <Container
      title="Results"
      color="#312244"
      coloredBars={false}
      folded={true}
    >
      {src ? (
        <iframe
          className={styles.dashboard}
          title="Experiment results"
          src={src}
        />
      ) : (
        <div className={styles.dashboardMissing}>
          The results dashboard is not configured.
        </div>
      )}
    </Container>
  );
}

// How the experiment ended or, while it is started and open, the ways to end
// it: promote a variant, or close with no winner. Either one saves the
// experiment (the Save runs after the confirmation, see Experiment).
function Outcome(props) {
  const { endpoint, stored, finishBlocker, onFinish } = props;
  const { values } = useFormikContext();
  const { requestConfirm } = useMainContext();
  const open = isStarted(stored) && isOpen(stored);

  const promote = useCallback(
    (variation) => {
      const current = pageTitle(endpoint?.blockId);

      requestConfirm(
        <>
          <p>
            Promote <b>{variation.label}</b> ({pageTitle(variation.blockId)}) of{" "}
            <b>{values.title}</b>?
          </p>
          <ul>
            {variation.control ? (
              <li>
                The control stays the default page of the endpoint{" "}
                {values.reference}: nothing changes there.
              </li>
            ) : (
              <li>
                Its page becomes the default page of the endpoint{" "}
                {values.reference}, in place of {current}.
              </li>
            )}
            <li>
              {variation.control
                ? "The other pages stay"
                : "The previous default page and the other pages stay"}{" "}
              as disabled variants of the endpoint. Nothing is deleted: delete
              the ones you don't need from the property.
            </li>
            <li>
              The experiment stops and is archived: it can't be restarted, and
              the endpoint is free for a new one.
            </li>
          </ul>
        </>,
        () => onFinish(promotedOutcome(variation))
      );
    },
    [
      endpoint?.blockId,
      onFinish,
      requestConfirm,
      values.reference,
      values.title
    ]
  );

  const close = useCallback(() => {
    requestConfirm(
      <>
        <p>
          Close <b>{values.title}</b> without a winner?
        </p>
        <ul>
          <li>The experiment stops and is archived: it can't be restarted.</li>
          <li>
            The endpoint and its pages stay as they are: the control is still
            its default page.
          </li>
        </ul>
      </>,
      () => onFinish(closedOutcome())
    );
  }, [onFinish, requestConfirm, values.title]);

  if (isArchived(stored)) {
    const { outcome } = stored;
    const winner = stored.variations?.find((v) => v.id === outcome.variationId);

    return (
      <Container
        title="Outcome"
        color="#2f2a14"
        coloredBars={false}
        folded={false}
      >
        <div className={styles.outcome}>
          {outcome.action === "promoted" ? (
            <p>
              <b>{winner?.label}</b> was promoted on {formatDate(outcome.at)}.
              {winner?.control
                ? " The control stays the default page of the endpoint."
                : " Its page is now the default page of the endpoint."}{" "}
              The other pages are disabled variants of the endpoint: delete the
              ones you don't need from the property.
            </p>
          ) : (
            <p>
              Closed without a winner on {formatDate(outcome.at)}. The endpoint
              was left as it was.
            </p>
          )}
        </div>
      </Container>
    );
  }

  if (!open) return null;

  return (
    <Container
      title="Outcome"
      color="#2f2a14"
      coloredBars={false}
      folded={false}
    >
      <div className={styles.outcome}>
        <p>
          Promote a variant to make its page the default page of the
          endpoint, or close the experiment with no winner. Either way the
          experiment is archived and can't be restarted.
        </p>
        {(stored.variations ?? []).map((variation) => (
          <div className={styles.promote} key={variation.id}>
            <span className={styles.promoteLabel}>
              {variation.control && (
                <FontAwesomeIcon icon={faFlask} className={styles.icon} />
              )}
              {variation.label}{" "}
              <span className={styles.hint}>
                ({pageTitle(variation.blockId)})
              </span>
            </span>
            <Button
              size="sm"
              variant="outline-warning"
              onClick={() => promote(variation)}
              disabled={Boolean(finishBlocker)}
            >
              Promote
            </Button>
          </div>
        ))}
        <div className={styles.promote}>
          <span className={styles.promoteLabel}>No winner</span>
          <Button
            size="sm"
            variant="outline-warning"
            onClick={close}
            disabled={Boolean(finishBlocker)}
          >
            Close without winner
          </Button>
        </div>
        {finishBlocker && <div className={styles.note}>{finishBlocker}</div>}
      </div>
    </Container>
  );
}

function Variations(props) {
  const { endpoint, stored } = props;
  const [autoTune, setAutoTune] = useState(false);
  const { setFieldValue, errors, values } = useFormikContext();
  const started = isStarted(stored);
  const archived = isArchived(stored);
  const locked = started || archived;
  const weights = hasWeights(values);

  const onChangeTraffic = useCallback(
    (index, value) => {
      const variations = values.variations.map((v) => ({ ...v }));
      const residuo = (100 - value) / 100;
      const rimasti = variations.length - 1;
      const percShare = (variations[index].traffic ?? 1) / rimasti;

      variations[index].traffic = value;

      if (autoTune) {
        variations.forEach((v, i) => {
          if (i !== index) {
            if (isNaN(v.traffic)) v.traffic = 1;
            const newPerc = v.traffic + percShare;
            v.traffic = Math.floor(residuo * newPerc);
          }
        });
      }

      setFieldValue("variations", variations);
    },
    [autoTune, setFieldValue, values.variations]
  );

  const handleAddVariant = useCallback(() => {
    const variations = values.variations ?? [];
    setFieldValue("variations", [...variations, newVariation(variations)]);
  }, [setFieldValue, values.variations]);

  const header = useMemo(() => {
    return (
      <div className={styles.variationHeader}>
        {(values?.variations || []).map((v, index) => (
          <div className={styles.variation} key={`${v.id}-${index}`}>
            {index === 0 && (
              <FontAwesomeIcon icon={faFlask} className={styles.icon} />
            )}
            {v.label}
            {weights && (
              <>
                <span className={styles.separator}>
                  <FontAwesomeIcon icon={faArrowRight} />
                </span>
                <span className={styles.value}>{v.traffic ?? 0}%</span>
              </>
            )}
          </div>
        ))}
      </div>
    );
  }, [values?.variations, weights]);

  const extra = useMemo(() => {
    if (locked || !weights) return null;

    return (
      <Form.Check
        type="switch"
        id="variationsAutotTune"
        checked={autoTune}
        value="true"
        onChange={(e) => setAutoTune(e.target.checked)}
        name="variationsAutotTune"
        label="Auto Adjust Traffic"
      />
    );
  }, [autoTune, locked, weights]);

  const footer = useMemo(() => {
    if (locked) return null;

    return (
      <div className={styles.footer}>
        <Button
          size="sm"
          variant="outline-success"
          onClick={handleAddVariant}
          disabled={!endpoint}
        >
          Add Variant
        </Button>
      </div>
    );
  }, [endpoint, handleAddVariant, locked]);

  return (
    <Container
      title="Variants"
      color="#272640"
      coloredBars={false}
      warning={errors.variations}
      header={header}
      footer={footer}
      extra={extra}
      folded={true}
      key={"hypo"}
    >
      {archived ? (
        <div className={styles.lockedNote}>
          The experiment is archived: it can't be changed.
        </div>
      ) : started ? (
        <div className={styles.lockedNote}>
          The experiment has started: the endpoint, the pages, the values and
          the traffic of the variants are locked. To change them, create a new
          experiment.
        </div>
      ) : !endpoint ? (
        <div className={styles.note}>
          Select an endpoint: the control is its default page.
        </div>
      ) : null}
      {(values?.variations || []).map((variation, index) => (
        <Variation
          data={variation}
          index={index}
          key={variation.id ?? index}
          onChangeTraffic={onChangeTraffic}
          endpoint={endpoint}
          locked={locked}
          archived={archived}
        />
      ))}
    </Container>
  );
}

const Variation = (props) => {
  const { data, index, onChangeTraffic, endpoint, locked, archived } = props;
  const { setFieldValue, errors, values } = useFormikContext();
  const control = index === 0;
  const weights = hasWeights(values);
  const pending = isPendingPage(
    data,
    (id) => db.peek("blocks", id) !== undefined
  );

  const path = `variations[${index}]`;

  // The control's page is the default one of the endpoint; the others', the
  // inactive variants nobody else uses, the one it has (also the one still
  // to create) always there
  const pages = useMemo(() => {
    const blockIds = selectablePages(endpoint, values.variations, index);

    if (data.blockId && !pending && !blockIds.includes(data.blockId))
      blockIds.unshift(data.blockId);

    return blockIds.map((blockId) => ({ blockId, title: pageTitle(blockId) }));
  }, [data.blockId, endpoint, index, pending, values.variations]);

  const onChangeLabel = useCallback(
    (e) => setFieldValue(`${path}.label`, e.target.value),
    [path, setFieldValue]
  );

  const onChangeValue = useCallback(
    (e) => setFieldValue(`${path}.value`, e.target.value),
    [path, setFieldValue]
  );

  // An existing page: the variant no longer creates one
  const onChangePage = useCallback(
    (e) => {
      const { blockId, created, ...rest } = data;
      const id = e.target.value;

      setFieldValue(path, id ? { ...rest, blockId: id } : rest);
    },
    [data, path, setFieldValue]
  );

  // A new page, created by the Save as a copy of the default page
  const onCreatePage = useCallback(() => {
    setFieldValue(path, { ...data, blockId: idGen(), created: true });
  }, [data, path, setFieldValue]);

  const handleDeleteVariant = useCallback(() => {
    const variations = [...values.variations];
    variations.splice(index, 1);
    setFieldValue("variations", variations);
  }, [index, setFieldValue, values.variations]);

  const header = useMemo(() => {
    return (
      <div className={styles.header}>
        {headerKW("Value", data.value)}
        {headerKW("Page", pending ? "new" : pageTitle(data.blockId))}
      </div>
    );
  }, [data.blockId, data.value, pending]);

  const footer = useMemo(() => {
    if (locked || control) return null;

    return (
      <div className={styles.footer}>
        <Button
          size="sm"
          variant="outline-danger"
          onClick={handleDeleteVariant}
        >
          Delete Variant
        </Button>
      </div>
    );
  }, [control, handleDeleteVariant, locked]);

  const extra = useMemo(() => {
    return (
      <div className={styles.extra}>
        {control && (
          <div className={styles.isBase}>
            <FontAwesomeIcon icon={faFlask} className={styles.icon} />
            <span className={styles.label}>CONTROL</span>
          </div>
        )}
        {weights && (
          <>
            <FontAwesomeIcon
              icon={faTrafficLight}
              className={`${styles.icon} ${
                errors.traffic ? styles.trafficError : ""
              } ${data?.traffic === 0 ? styles.trafficOff : ""}`}
            />
            <span className={styles.traffic}>{data.traffic ?? 0}%</span>
          </>
        )}
      </div>
    );
  }, [control, data.traffic, errors.traffic, weights]);

  return (
    <Container
      title={data.label}
      folded={true}
      key={data.id}
      id={data.id}
      color={control ? "#201500" : "#060f01"}
      warningMessage={errors[data.id]}
      coloredBars={!control}
      header={header}
      headerOnlyOnFolded={false}
      footer={footer}
      extra={extra}
    >
      <div className={styles.variantBox}>
        <div className={styles.row}>
          <div className={styles.column}>
            <div className={styles.labelCol}>Name</div>
            <div className={styles.contentCol}>
              <Form.Control
                type="text"
                placeholder="Enter name"
                isInvalid={Boolean(errors[`${data.id}:label`])}
                value={data?.label ?? ""}
                onChange={onChangeLabel}
                disabled={archived}
              />
            </div>
          </div>
          <div className={`${styles.column} ${styles.small}`}>
            <div className={styles.labelCol}>Value</div>
            <div className={styles.contentCol}>
              <Form.Control
                type="text"
                placeholder="a"
                isInvalid={Boolean(errors[`${data.id}:value`])}
                value={data?.value ?? ""}
                onChange={onChangeValue}
                disabled={locked}
              />
              {fieldError(errors[`${data.id}:value`])}
            </div>
          </div>
          <div className={styles.column}>
            <div className={styles.trafficKnob}>
              {weights && !locked && (
                <RotaryKnob
                  value={data?.traffic}
                  min={0}
                  max={100}
                  step={1}
                  label="Traffic Percentage"
                  labelPosition="right"
                  skin="s12"
                  onChange={(value) => onChangeTraffic(index, value)}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.column}>
            <div className={styles.labelCol}>Page</div>
            <div className={styles.contentCol}>
              {control ? (
                <Form.Control
                  type="text"
                  readOnly
                  disabled
                  value={pageTitle(data.blockId)}
                />
              ) : (
                <div className={styles.pageRow}>
                  <Form.Control
                    as="select"
                    value={data.blockId ?? ""}
                    onChange={onChangePage}
                    isInvalid={Boolean(errors[`${data.id}:page`])}
                    disabled={locked || !endpoint}
                  >
                    <option value=""></option>
                    {pages.map(({ blockId, title }) => (
                      <option key={blockId} value={blockId}>
                        {title || blockId}
                      </option>
                    ))}
                    {pending && (
                      <option value={data.blockId}>
                        New page (created on Save)
                      </option>
                    )}
                  </Form.Control>
                  {!locked && !pending && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={onCreatePage}
                      disabled={!endpoint}
                    >
                      Create page
                    </Button>
                  )}
                </div>
              )}
              {fieldError(errors[`${data.id}:page`])}
              {control && (
                <div className={styles.note}>
                  The default page of the endpoint.
                </div>
              )}
              {pending && (
                <div className={styles.note}>
                  The page is created when the experiment is saved, as a copy of
                  the default page of the endpoint, and is added to its variants
                  (disabled). Edit it afterwards from the property.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};
